/**
 * What to say to a person whose room was refused or closed (`room.refusal.reason`, CONTRACTS §2,
 * §16). The words are Artwork's; the reasons are collab's and the gateway's.
 */

/** A file in another workspace answers "not found" on purpose, so ids from elsewhere reveal nothing. */
export const NOT_THERE = "This file doesn't exist, or it isn't in this workspace.";

export function refusalText(reason: string | undefined): string {
  switch (reason) {
    case 'deleted':
      return 'This file was deleted.';
    case 'access-removed':
      return 'You no longer have access to this file.';
    case 'removed':
      return 'This page was removed.';
    case 'room-full':
      return 'This file has too many people in it right now. Try again in a moment.';
    case 'app-disabled':
    case 'switched-off':
      return 'This file can’t be opened right now. Try again in a moment.';
    case 'forbidden':
    case 'permission-denied':
    case 'unknown-room':
    case 'bad-room':
    case 'wrong-room':
      return NOT_THERE;
    case undefined:
    case '':
      return NOT_THERE;
    default:
      return 'This file could not be opened.';
  }
}

/**
 * With the kill switch on (`VITE_LIVE_ARTWORK=off`) a file opens only from this device's copy:
 * there is no read of the stored state to start from, so a file the device has never held cannot
 * open. Said as `switched-off`, it is not a refusal by the server.
 */
export const SWITCHED_OFF = 'switched-off';

/** A refusal that ends editing even though the file was open: someone took it away. */
export const stopsEditing = (reason: string | undefined): boolean => reason === 'deleted' || reason === 'access-removed' || reason === 'permission-denied' || reason === 'forbidden';

/**
 * A refusal that means "you are signed in but not let in" — taken off the file, or never on it:
 * the access screen with Request access answers it (SHARING-MODEL §3.3), never a dead end.
 * A file that does not exist answers `unknown-room`, and a deleted one `deleted`: those keep
 * their sentence.
 */
export const needsAccess = (reason: string | undefined): boolean =>
  reason === 'access-removed' || reason === 'permission-denied' || reason === 'forbidden';
